import { Controller } from "@hotwired/stimulus";

import { DESKTOP_MIN_WIDTH, prefersReducedMotion } from "~/lib/viewport";
import { clamp01 } from "~/lib/web/ease";
import { registerProbe } from "~/lib/web/scrub";

/** The corner the frame carries once it has landed, in px. */
const RADIUS = 24;
/**
 * How much longer the travel is than the scroll it takes, which is what keeps
 * the descent monotone at every window size.
 *
 * The frame's position on screen is -s + dy * e(s / D), for scroll s, travel dy
 * and runway D. Differentiating, it descends only while e'(t) >= D / dy, so the
 * ease's derivative has a hard floor set by the ratio of the two distances.
 * Pinning the ratio here makes that floor a constant the ease can be built
 * against; deriving the runway from the viewport instead would move it with
 * every window height. 1.8 puts the floor at 0.556.
 */
const TRAVEL_RATIO = 1.8;
/**
 * The fraction of the move over which the copy goes. Early, because it is cream
 * type set for a full-bleed photograph and the ground behind it turns to the
 * page's white as the frame leaves.
 */
const COPY_OUT = 0.32;
/**
 * What is left of the scrim once the copy is gone. Not zero: the frame would
 * reach full brightness a third of the way through the move, which reads as the
 * photograph switching on.
 */
const SCRIM_FLOOR = 0.28;
/**
 * The ease's amplitude, and the reason it is a sine-deviated ramp rather than
 * one of the two obvious curves. Cubic ease-in-out is flat at the start, so the
 * transform contributes nothing while the page scrolls and the frame rises
 * before it falls; cubic ease-out is flat at the finish, so having descended too
 * fast it has to climb back to a target that has been scrolling up all along.
 * Both reverse, in different places.
 *
 * This one's derivative is 1 - A * cos(2 pi t), which stays inside [1 - A,
 * 1 + A] and is never zero. At 0.3 that is [0.7, 1.3], comfortably above the
 * 0.556 floor TRAVEL_RATIO sets, so the descent is monotone from the first pixel
 * to the last, and it is still eased: slowest at both ends, fastest through the
 * middle.
 */
const EASE_AMPLITUDE = 0.3;

const ease = (progress: number): number =>
  progress -
  (EASE_AMPLITUDE / (2 * Math.PI)) * Math.sin(2 * Math.PI * progress);

interface Flip {
  /** Where the frame's visible top left corner has to end up, relative to its
   * own. */
  dx: number;
  dy: number;
  /** What the frame has to be scaled by to cover the landing box. */
  scale: number;
  /** What to crop off each pair of edges, in the frame's own space. */
  insetX: number;
  insetY: number;
  /** The scroll the move takes, in px. */
  runway: number;
}

/**
 * The hero photograph scales down, travels down, and comes to rest exactly on a
 * box in the section below it.
 *
 * The frame and that box are both in normal flow on the same page, so they
 * scroll together and the offset between them never changes. The transform that
 * maps one onto the other is therefore constant: measure it once, and the only
 * thing scroll decides is how much of it to apply. That is what lets this land
 * on something rather than stopping at a guessed offset, with no pinned stage,
 * no travelling fixed layer, and no handover to a real element at the end.
 */
export default class WebHandoffController extends Controller<HTMLElement> {
  static override targets = ["frame", "scrim", "copy"];
  static override values = { landing: String };
  declare readonly frameTarget: HTMLElement;
  declare readonly scrimTarget: HTMLElement;
  declare readonly copyTarget: HTMLElement;
  declare readonly landingValue: string;

  private unregister: (() => void) | null = null;
  private listeners = new AbortController();
  private flip: Flip | null = null;

  override connect(): void {
    // Below the desktop width the photograph is close to the viewport's own
    // width and the box it would land on is 420 wide, so the travel is most of
    // the way to a thumbnail. The still hero is what a phone gets, and the
    // scroll probe is not registered at all.
    if (prefersReducedMotion() || window.innerWidth < DESKTOP_MIN_WIDTH) return;
    const landing = document.querySelector(this.landingValue);
    if (landing === null) return;

    this.measure(landing);
    // Both move the box: a resize changes the lane it sits in, and a font
    // swapping changes the wrap of the heading under it.
    window.addEventListener("resize", () => this.measure(landing), {
      signal: this.listeners.signal,
    });
    void document.fonts?.ready.then(() => this.measure(landing));

    this.unregister = registerProbe({
      read: () => {
        // Retaken while at rest, and only there. The mount measurement goes
        // stale as soon as anything below it reflows, and retaking it mid-flight
        // would make the picture jump.
        if (window.scrollY <= 0) this.measure(landing);
        return window.scrollY;
      },
      write: (scrollY) => this.apply(scrollY),
    });
  }

  override disconnect(): void {
    this.unregister?.();
    this.unregister = null;
    this.listeners.abort();
    this.listeners = new AbortController();
    this.flip = null;
  }

  /**
   * Both rects taken in the same frame, so the offset between them is the one
   * the transform has to close whatever the scroll position.
   *
   * The transform comes off for the read and goes straight back: a measured rect
   * of a transformed element is the transformed rect, so measuring mid-flight
   * would compound. Restored before returning, so nothing paints in between.
   */
  private measure(landing: Element): void {
    const element = this.frameTarget;
    const applied = element.style.transform;
    element.style.transform = "none";
    const frame = element.getBoundingClientRect();
    const target = landing.getBoundingClientRect();
    element.style.transform = applied;
    if (!frame.width || !target.width) return;
    // Two properties, two jobs. The frame is the viewport's shape and the box is
    // the design's, and those ratios never match, so one uniform scale cannot
    // satisfy both axes and scaling them separately would stretch the
    // photograph. The scale covers the box on whichever axis needs the most and
    // a clip-path crops the excess off the other, which is object-fit: cover
    // expressed as a transform.
    const scale = Math.max(
      target.width / frame.width,
      target.height / frame.height,
    );
    // clip-path resolves before the transform, in the frame's own unscaled
    // space, so what should read as the excess on screen is that over the scale.
    const insetX = Math.max(
      0,
      (frame.width * scale - target.width) / scale / 2,
    );
    const insetY = Math.max(
      0,
      (frame.height * scale - target.height) / scale / 2,
    );
    // Minus the crop: the clip takes the inset off the top and the left as well,
    // so the frame's visible corner sits that far inside its layout corner, and
    // it is the visible corner that has to land on the box.
    const dy = target.top - frame.top - insetY * scale;
    this.flip = {
      dx: target.left - frame.left - insetX * scale,
      dy,
      scale,
      insetX,
      insetY,
      runway: Math.max(1, dy / TRAVEL_RATIO),
    };
  }

  private apply(scrollY: number): void {
    const flip = this.flip;
    if (flip === null) return;
    const progress = ease(clamp01(scrollY / flip.runway));
    const scale = 1 - (1 - flip.scale) * progress;
    const frame = this.frameTarget;
    frame.style.transform =
      `translate(${(flip.dx * progress).toFixed(1)}px,` +
      ` ${(flip.dy * progress).toFixed(1)}px) scale(${scale.toFixed(5)})`;
    // Both terms take the progress, not just the crop: at rest the inset is zero
    // but a constant radius is not, so the photograph would fill the viewport
    // with four rounded corners and the page showing through each one. Both also
    // divide by the scale, for the same reason the inset does, which renders the
    // corner as a true RADIUS once it has landed.
    frame.style.clipPath =
      `inset(${(flip.insetY * progress).toFixed(1)}px` +
      ` ${(flip.insetX * progress).toFixed(1)}px` +
      ` round ${((RADIUS * progress) / scale).toFixed(1)}px)`;
    const scrim = 1 - (progress / COPY_OUT) * (1 - SCRIM_FLOOR);
    this.scrimTarget.style.opacity = Math.max(SCRIM_FLOOR, scrim).toFixed(3);
    const copy = clamp01(1 - progress / COPY_OUT);
    this.copyTarget.style.opacity = copy.toFixed(3);
    // opacity: 0 still hit-tests, so invisible cream type would keep taking
    // clicks and selections over the copy of the section below it.
    this.copyTarget.style.pointerEvents = copy > 0 ? "" : "none";
  }
}
